import { useRef } from 'react';

import { SelectionInfo } from './useSelectionData.type';

import {
  getTargetRange,
  checkIsInsideHighlightSpan,
  checkIsIncludingInlineCode,
  getValidTag,
  calculatePureTextStartIndex,
  getSplitContentLengthTable,
  getSelectionRange,
  getSelectedText,
  getTagSums,
} from './useSelectionData.utils';

import {
  convertHTML,
  findAllValidElements,
  isValidElement,
} from '@/shared/utils';

const useSelectionData = (targetTag: string) => {
  const infoRef = useRef<SelectionInfo | null>(null);
  const setInfo = (info: SelectionInfo | null) => {
    infoRef.current = info;
  };
  const resetInfo = () => (infoRef.current = null);

  const getTagIndex = (node: Node): number => {
    if (typeof window === 'undefined') return -1;

    const parent = document.querySelector(targetTag);
    if (!parent) return -1;

    const getNearestValidElement = (
      element: Element | null
    ): Element | null => {
      if (!element || element === parent) return null;
      if (isValidElement(element)) return element;
      return getNearestValidElement(element.parentElement);
    };

    // TODO: Element가 아닌 경우의 수 파악하여 조건 수정 필요
    const topLevelParent =
      node instanceof Element
        ? getNearestValidElement(node)
        : getNearestValidElement(node.parentElement);

    if (!topLevelParent) return -1;

    const allValidElements = findAllValidElements(parent);
    return allValidElements.indexOf(topLevelParent);
  };

  const getSelectionInfo = (content: string) => {
    if (window === undefined) return;

    const range = getSelectionRange();
    if (!range) return;

    const selectedText = getSelectedText(range);
    if (!selectedText) return;

    const startTagIndex = getTagIndex(range.startContainer);
    const endTagIndex = getTagIndex(range.endContainer);
    const container = range.commonAncestorContainer;

    const isValidSelection =
      startTagIndex === endTagIndex &&
      !getTargetRange(range).some(checkIsInsideHighlightSpan) &&
      !checkIsInsideHighlightSpan(container) &&
      !checkIsIncludingInlineCode(container, selectedText);
    if (!isValidSelection) return resetInfo();

    const topLevelValidTag = getValidTag(range.startContainer, targetTag);
    if (!topLevelValidTag) return;

    const pureText = range.toString();
    const pureTextLength = pureText.length;
    const pureTextStartIndex = calculatePureTextStartIndex(
      topLevelValidTag,
      range
    );
    const pureTextEndIndex = pureTextStartIndex + pureTextLength;

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = content;
    const validElements = findAllValidElements(tempContainer);
    const selectedBlock = convertHTML(validElements[startTagIndex].innerHTML);

    //선택 영역이 포함된 문단을 태그와 텍스트로 나누어 그 길이를 테이블로 만듦
    const splitContentLengthTable = getSplitContentLengthTable(selectedBlock);

    //선택 영역의 시작,끝 이전의 모든 태그 길이의 합을 구함
    const { startTagSum, endTagSum } = getTagSums(
      splitContentLengthTable,
      pureTextStartIndex,
      pureTextEndIndex
    );

    //선택 영역의 시작,끝 인덱스 계산
    const startOffset = pureTextStartIndex + startTagSum;
    const endOffset = pureTextEndIndex + endTagSum;

    //선택 영역의 텍스트 추출
    const text = selectedBlock.slice(startOffset, endOffset);

    const generatedSelectionInfo = {
      text,
      startTagIndex,
      endTagIndex,
      startOffset,
      endOffset: range.endOffset,
      selectedLength: endOffset - startOffset,
      pureTextLength,
    };

    setInfo(generatedSelectionInfo);

    return generatedSelectionInfo;
  };

  return {
    selectionInfo: infoRef.current,
    getSelectionInfo,
    getTagIndex,
  };
};

export default useSelectionData;
